// Reference debuffs. One per hook a debuff can use: a timed condition from the
// trait table, and a timer that fires at the start of the owner's turn.

import {
  EXPLODE_TURNS,
  LOST_ENERGY_TURNS,
  STOP_TURNS,
  STOPPED_PENALTY_RATIO,
} from '../../constants';
import { ABILITY_NAMES, kindForAbilities } from '../../engine/abilities';
import { demote } from '../../engine/apply';
import { emptySquares, isSide, otherSide } from '../../engine/pieces';
import { isOnOwnHalf, neighborSquares, squareName } from '../../engine/squares';
import { isSolid } from '../../engine/terrain';
import type { Piece, Side } from '../../engine/types';
import type { GameState } from '../../game/state';
import {
  bestCaptureValue,
  canMoveNow,
  casterCanTake,
  defineMechanic,
  describePiece,
  enemyPieces,
  isFuseLit,
  noEffect,
  OWNER_NAMES,
  pieceAt,
  relocatePiece,
  say,
  settleLanding,
  squaresWhere,
  TAKEN_SOON_PENALTY,
  updatePiece,
  valueAt,
} from '../helpers';
import { markEnemy, PICK_ENEMY } from '../kit';
import type { SpellMechanic } from '../types';

/**
 * The piece to stop. A piece the caster can take this turn is gone before the
 * stop does anything, so it comes last. A piece that cannot move now loses
 * little. A piece that can take one of the caster's loses that capture too.
 */
const stopGain = (state: GameState, square: number, caster: Side): number => {
  const value = valueAt(state, square);
  if (casterCanTake(state, square, caster)) return value - TAKEN_SOON_PENALTY;
  if (!canMoveNow(state, square)) return value * STOPPED_PENALTY_RATIO;
  return value + bestCaptureValue(state, square);
};

/**
 * Hook: a timed trait. The trait table ticks it at the start of the owner's
 * turn, draws the badge with the count, and logs the expiry. A debuff on an
 * enemy piece carries a plus one, because the first tick comes before the
 * enemy can act.
 */
export const stopPiece = markEnemy({
  id: 'stop-piece',
  family: 'status',
  summary: `The enemy piece cannot move for ${STOP_TURNS} of their turns.`,
  prompt: PICK_ENEMY,
  eligible: (piece) => piece.stoppedTurns === 0,
  patch: { stoppedTurns: STOP_TURNS + 1 },
  vars: { turns: STOP_TURNS },
  lines: { cast: '{who} cannot move for {turns} turns.' },
  aiScore: (state, square, caster) => stopGain(state, square, caster),
});

/**
 * Hook: a timer that fires. flow.ts detonates a piece whose timer runs out,
 * with its eight neighbors, and records an event. The search knows to keep
 * its pieces away from a piece about to explode. The wheel does not bring it
 * back while a fuse is lit, so the face of defuse-bomb stays until then.
 */
export const explodeLater: SpellMechanic = {
  ...markEnemy({
    id: 'explode-later',
    family: 'timer',
    summary: `The enemy piece explodes in ${EXPLODE_TURNS} of their turns, with its eight neighbors.`,
    prompt: 'Pick an enemy piece that is not royal.',
    eligible: (piece) => !piece.royal && piece.explodeIn === 0,
    patch: { explodeIn: EXPLODE_TURNS + 1 },
    vars: { turns: EXPLODE_TURNS },
    lines: { cast: '{who} explodes in {turns} turns, with its neighbors.' },
    // The piece whose blast would take the most of its own side with it. A
    // piece the caster can take this turn comes last: the capture removes the timer.
    aiScore: (state, square, caster) => {
      const victim = state.board[square];
      if (!victim) return 0;
      const crowd = neighborSquares(square)
        .filter((n) => state.board[n]?.owner === victim.owner)
        .reduce((sum, n) => sum + valueAt(state, n), 0);
      const wasted = casterCanTake(state, square, caster) ? TAKEN_SOON_PENALTY : 0;
      return valueAt(state, square) + crowd - wasted;
    },
  }),
  canDeal: (_wheel, board) => !isFuseLit(board),
};

/** A piece with more than pawn steps. A blocker and a dormant piece have no ability to lose. */
const canLoseAbility = (piece: Piece): boolean =>
  piece.abilities.some((ability) => ability !== 'pawn');

/**
 * Seed from Rob: "Downgrade an enemy piece by one move type".
 * Hook: abilities, taken away. A random ability goes. The silhouette follows
 * the set that is left. Rob, 2026-10-12: "If a piece has only one ability
 * that gets removed, it becomes a pawn. Pawns cannot be picked." A royal
 * piece stays royal.
 */
export const pieceDowngrade = defineMechanic({
  id: 'piece-downgrade',
  polarity: 'enemy',
  family: 'ability',
  targetCount: 1,
  spec: {
    summary:
      'The enemy piece loses one of its abilities, chosen at random. A piece that loses its only ability becomes a pawn. A pawn cannot be picked.',
    prompt: 'Pick an enemy piece that is not a pawn.',
    lines: {
      cast: '{who} loses {ability}.',
      castPawn: '{who} loses {ability}. It is a pawn now.',
    },
  },
  // The piece with the most to lose. A piece the caster can take this turn comes last.
  aiScore: (state, square, caster) =>
    valueAt(state, square) - (casterCanTake(state, square, caster) ? TAKEN_SOON_PENALTY : 0),
  eligible: (state, caster) => enemyPieces(state, caster, canLoseAbility),
  cast: ({ state, rng, self }, [square]) => {
    if (square === undefined) return state;
    const piece = pieceAt(state, square);
    const lost = rng.pick(piece.abilities);
    const left = piece.abilities.filter((ability) => ability !== lost);
    const vars = { who: describePiece(piece, square), ability: ABILITY_NAMES[lost] };
    if (left.length === 0) {
      return say(updatePiece(state, square, demote(piece)), self, 'castPawn', vars);
    }
    const changed = {
      abilities: left,
      gained: piece.gained.filter((ability) => ability !== lost),
      kind: kindForAbilities(left, piece.kind),
    };
    return say(updatePiece(state, square, changed), self, 'cast', vars);
  },
});

const enemyPawns = (state: GameState, caster: Side): number[] =>
  enemyPieces(state, caster, (piece) => piece.kind === 'pawn' && !piece.royal);

/**
 * Hook: a piece changes owner. Everything about the piece stays. It was a
 * backfire that gave a pawn of the caster away. A royal pawn is not in the
 * draw: it could end the match.
 */
export const pawnChangesSide = defineMechanic({
  id: 'pawn-changes-side',
  polarity: 'enemy',
  family: 'placement',
  spec: {
    summary: 'One enemy pawn that is not royal, chosen at random, joins your side.',
    prompt: '',
    lines: { cast: '{who} changes sides.', noEffect: 'No pawn was left to change sides.' },
  },
  canCast: (state, caster) => enemyPawns(state, caster).length > 0,
  cast: ({ state, caster, rng, self }) => {
    const pawns = enemyPawns(state, caster);
    if (pawns.length === 0) return noEffect(state, self);
    const square = rng.pick(pawns);
    const piece = pieceAt(state, square);
    const next = updatePiece(state, square, { owner: caster, moved: true });
    return say(next, self, 'cast', { who: describePiece(piece, square) });
  },
});

/** The empty squares on the half of the enemy of `owner`. A deadly square is one of them. */
const enemyHalfSquares = (state: GameState, owner: Side): number[] =>
  emptySquares(state.board).filter(
    (square) => !isOnOwnHalf(square, owner) && !isSolid(state.rules, square),
  );

/** The side of the piece on the square. Null for no piece, and for a piece of no side. */
const sideAt = (state: GameState, square: number): Side | null => {
  const owner = state.board[square]?.owner;
  return owner !== undefined && isSide(owner) ? owner : null;
};

/**
 * Seed from Rob: "Transport to a random empty space on the enemy side".
 * Hook: relocation with one pick. The caster picks any piece that is not
 * royal, of either side, and the die picks an empty square on the half of
 * the enemy of that piece. Rob, 2026-10-12: "It should be applicable to any
 * non-royal piece on the board". A piece of the caster goes to the enemy
 * half. An enemy piece goes to the half of the caster. The stop after a cast
 * keeps a piece of the caster there for the rest of the turn. An enemy piece
 * is free on its next turn. A deadly square is in the
 * draw. It was a backfire on a piece of the caster, then a debuff on an
 * enemy piece only. A royal piece is not a target. The cast is the whole turn
 * of the caster. Rob, 2026-10-13: "Let's have Enemy Lines end the caster's
 * turn entirely after the move."
 */
export const enemyLines = defineMechanic({
  id: 'enemy-lines',
  polarity: 'any',
  family: 'placement',
  targetCount: 1,
  endsTurn: true,
  // An enemy piece, the most valuable first. The AI does not send a piece of its own.
  aiScore: (state, square, caster) =>
    sideAt(state, square) === caster ? -valueAt(state, square) : valueAt(state, square),
  spec: {
    summary:
      'A piece that is not royal, yours or the enemy’s, goes to a random empty square on the half of its enemy. A deadly square removes it. Your turn ends.',
    prompt: 'Pick a piece that is not royal, yours or the enemy’s.',
    lines: { cast: '{who} goes to {square}.' },
  },
  eligible: (state) =>
    squaresWhere(state, (piece) => !piece.royal && isSide(piece.owner)).filter((square) => {
      const owner = sideAt(state, square);
      return owner !== null && enemyHalfSquares(state, owner).length > 0;
    }),
  cast: ({ state, rng, self }, [from]) => {
    const owner = from === undefined ? null : sideAt(state, from);
    if (from === undefined || owner === null) return state;
    const squares = enemyHalfSquares(state, owner);
    if (squares.length === 0) return state;
    const to = rng.pick(squares);
    const piece = pieceAt(state, from);
    const next = { ...state, board: relocatePiece(state.board, from, to) };
    const said = say(next, self, 'cast', {
      who: describePiece(piece, from),
      square: squareName(to),
    });
    return settleLanding(said, to);
  },
});

/**
 * Seed from Rob: "Don't gain energy for two turns".
 * Hook: the energy of a side. The flow skips the gain at the start of the
 * next turns of the enemy, and records an event each time. In Godsmode the
 * enemy does not roll the die on those turns. It was a backfire on the caster.
 */
export const lostEnergy = defineMechanic({
  id: 'lost-energy',
  polarity: 'enemy',
  family: 'timer',
  spec: {
    summary: `The enemy gains no energy on their next ${LOST_ENERGY_TURNS} turns.`,
    prompt: '',
    lines: { cast: '{owner} gains no energy on the next {turns} turns.' },
  },
  cast: ({ state, caster, self }) => {
    const enemy = otherSide(caster);
    const energyHold = {
      ...state.energyHold,
      [enemy]: state.energyHold[enemy] + LOST_ENERGY_TURNS,
    };
    return say({ ...state, energyHold }, self, 'cast', {
      owner: OWNER_NAMES[enemy],
      turns: LOST_ENERGY_TURNS,
    });
  },
});

export const DEBUFFS: readonly SpellMechanic[] = [
  stopPiece,
  explodeLater,
  pieceDowngrade,
  pawnChangesSide,
  enemyLines,
  lostEnergy,
];
